import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ElearningCourseCard from '../ElearningCourseCard';
import WebinarsEventsModal from '../WebinarsEventsModal';
import { MODULES_CATALOG } from '../../data/modulesCatalog';
import { useAuth } from '../../contexts/AuthContext';
import { markModuleProgressCompleted } from '../../services/lmsData';
import './Dashboard.css';

const WORKPLACE_MODULE_STAGE_IDS = [
  'overview',
  'anika-story',
  'barriers',
  'meta-reflexivity',
  'meta-reflexive-manager',
  'actions',
  'workplace-survey',
];

const EDUCATOR_MODULE_STAGE_IDS = [
  'overview',
  'anika-story',
  'barriers',
  'meta-reflexivity',
  'meta-reflexive-manager',
  'actions',
  'educator-survey',
];

const COMMUNITY_MODULE_STAGE_IDS = [
  'overview',
  'anika-story',
  'barriers',
  'meta-reflexivity',
  'meta-reflexive-manager',
  'actions',
  'community-survey',
];

const PARENT_MODULE_STAGE_IDS = [
  'overview',
  'sara-story',
  'anika-story',
  'aman-story',
  'meta-reflexivity',
  'parent-survey',
];

function hasLocalSurveySubmission(moduleId) {
  try {
    const key =
      moduleId === 'community-module'
        ? 'lms-community-module-survey-submissions'
        : moduleId === 'educator-module'
          ? 'lms-educator-module-survey-submissions'
          : moduleId === 'parent-module'
            ? 'lms-parent-module-survey-submissions'
            : 'lms-workplace-module-survey-submissions';
    const rows = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(rows) && rows.length > 0;
  } catch {
    return false;
  }
}

const Dashboard = ({ user }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [webinarsModalOpen, setWebinarsModalOpen] = useState(false);
  const [workplaceConsentSignal, setWorkplaceConsentSignal] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { moduleProgress, refreshModuleProgress, firebaseUser } = useAuth();

  useEffect(() => {
    // After returning from a module, reload progress so the cards reflect the latest writes.
    void refreshModuleProgress?.();
  }, [refreshModuleProgress]);

  /** Heal survey-finished modules stuck at 100% in-progress (pre-fix Firestore writes). */
  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || !moduleProgress?.length) return;
    const healTargets = [
      { moduleId: 'workplace-module', stageIds: WORKPLACE_MODULE_STAGE_IDS, finalStageId: 'workplace-survey' },
      { moduleId: 'educator-module', stageIds: EDUCATOR_MODULE_STAGE_IDS, finalStageId: 'educator-survey' },
      { moduleId: 'community-module', stageIds: COMMUNITY_MODULE_STAGE_IDS, finalStageId: 'community-survey' },
      { moduleId: 'parent-module', stageIds: PARENT_MODULE_STAGE_IDS, finalStageId: 'parent-survey' },
    ];
    for (const target of healTargets) {
      const prog = moduleProgress.find((p) => p.moduleId === target.moduleId);
      if (!prog || prog.status === 'completed' || prog.isCompleted) continue;
      if ((Number(prog.progressPercent) || 0) < 100) continue;
      if (!hasLocalSurveySubmission(target.moduleId)) continue;
      void markModuleProgressCompleted(uid, target.moduleId, {
        finalStageId: target.finalStageId,
        totalStages: target.stageIds.length,
        allStageIds: target.stageIds,
      }).then(() => refreshModuleProgress?.());
    }
  }, [firebaseUser?.uid, moduleProgress, refreshModuleProgress]);

  /** Introduction module "Continue to workplace" sends users here so the same enrollment consent UI can run. */
  useEffect(() => {
    if (!location.state?.openWorkplaceConsent) return;
    setWorkplaceConsentSignal((n) => n + 1);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const modules = MODULES_CATALOG.map((m) => {
    const progressDoc = moduleProgress?.find((p) => p.moduleId === m.id);
    if (!progressDoc) return m;
    return {
      ...m,
      status: progressDoc.status || m.status,
      isEnrolled: Boolean(progressDoc.isEnrolled || progressDoc.status === 'in-progress' || progressDoc.status === 'completed'),
      isCompleted: Boolean(progressDoc.isCompleted || progressDoc.status === 'completed'),
      progress: Number(progressDoc.progressPercent) || 0,
      currentStageId: progressDoc.currentStageId || null,
      completedStagesCount: progressDoc.completedStagesCount || 0,
      totalStages: progressDoc.totalStages || 0,
    };
  });

  const filteredModules = modules.filter(
    (m) =>
      !searchQuery.trim() ||
      m.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const displayLimit = showAllCourses ? 999 : 6;
  const displayModules = filteredModules.slice(0, displayLimit);
  const hasMore = filteredModules.length > displayLimit;

  return (
    <div className="dashboard">
      {/* Full-width Learning Hub bar */}
      <div className="dashboard-content-bar">
        <div className="dashboard-content-bar-inner">
          {/* <span className="content-bar-icon" aria-hidden="true">🏠</span> */}
          <h1 className="content-bar-title">Learning Hub</h1>
        </div>
      </div>

      <div className="dashboard-container">
        {/* Section 1: Three CTA cards */}
        <section className="dashboard-cta-section">
          {/* <h2 className="dashboard-cta-heading">
            Breaking Barriers Research Project
          </h2> */}
          <div className="cta-cards">
            <Link to="/resources" className="cta-card cta-card-resources">
              <h3 className="cta-card-title">Resources</h3>
              <span className="cta-card-btn">BROWSE</span>
            </Link>
            <button
              type="button"
              className="cta-card cta-card-events"
              onClick={() => setWebinarsModalOpen(true)}
            >
              <h3 className="cta-card-title">Webinars & Events</h3>
              <span className="cta-card-btn">BROWSE</span>
            </button>
            <Link to="/nvivo-dashboard" className="cta-card cta-card-nvivo">
              <h3 className="cta-card-title">NVIVO Dashboard</h3>
              <span className="cta-card-btn">OPEN</span>
            </Link>
          </div>
        </section>

        <WebinarsEventsModal open={webinarsModalOpen} onClose={() => setWebinarsModalOpen(false)} />

        {/* Section 2: eLearning banner */}
        {/* <section className="dashboard-elearning-banner">
          <h2 className="elearning-banner-title">eLearning</h2>
        </section> */}

        {/* Section 3: eLearning course list - two columns */}
        <section className="dashboard-elearning-section">
          {/* <div className="elearning-toolbar">
            <button type="button" className="elearning-toolbar-btn" aria-label="Filter">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            </button>
            <div className="elearning-search-wrap">
              <svg className="elearning-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search content in the platform"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="elearning-search-input"
                aria-label="Search courses"
              />
            </div>
            <span className="elearning-item-count">{filteredModules.length} Items</span>
          </div> */}

          <div className="elearning-grid">
            {displayModules.map((module, index) => (
              <ElearningCourseCard
                key={module.id}
                module={module}
                imagePriority={index === 0}
                workplaceConsentSignal={module.id === 'workplace-module' ? workplaceConsentSignal : 0}
              />
            ))}
          </div>

          {hasMore && (
            <div className="elearning-load-more-wrap">
              <button
                type="button"
                className="elearning-load-more"
                onClick={() => setShowAllCourses(true)}
              >
                Load more
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
